/**
 * Input validation utilities
 * Reusable validation functions for form inputs
 *
 * @module utils/validation
 */

import { parseNumberExpression } from './numberParsing';
import { VALIDATION } from '../constants';

export interface ValidationResult {
  valid: boolean;
  error?: string;
  value?: number | null;
}

export interface ValidateNumberOptions {
  min?: number;
  max?: number;
  required?: boolean;
  allowNegative?: boolean;
}

export interface SanitizeStringOptions {
  maxLength?: number;
}

/**
 * Validate email format
 *
 * @example
 * isValidEmail('user@example.com')  // true
 * isValidEmail('invalid-email')     // false
 */
export function isValidEmail(email: string): boolean {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Validate password strength
 *
 * @example
 * validatePassword('short')     // {valid: false, error: 'Password must be at least 8 characters'}
 * validatePassword('longenough') // {valid: true}
 */
export function validatePassword(
  password: string,
  minLength = VALIDATION.MIN_PASSWORD_LENGTH,
): ValidationResult {
  if (!password || typeof password !== 'string') {
    return { valid: false, error: 'Password is required' };
  }

  if (password.length < minLength) {
    return {
      valid: false,
      error: `Password must be at least ${minLength} characters`,
    };
  }

  return { valid: true };
}

/**
 * Validate element size (min/max mesh element size)
 *
 * @example
 * validateElementSize('10.5', 0.01, 1000)  // {valid: true, value: 10.5}
 * validateElementSize('-5', 0.01, 1000)     // {valid: false, error: 'Value must be positive'}
 */
export function validateElementSize(
  value: string | number,
  min = VALIDATION.MIN_ELEMENT_SIZE_VALUE,
  max = VALIDATION.MAX_ELEMENT_SIZE_VALUE,
): ValidationResult {
  const parsed = parseNumberExpression(value);

  if (isNaN(parsed) || !isFinite(parsed)) {
    return { valid: false, error: 'Invalid number' };
  }

  if (parsed <= 0) {
    return { valid: false, error: 'Value must be positive' };
  }

  if (parsed < min) {
    return { valid: false, error: `Value must be at least ${min}` };
  }

  if (parsed > max) {
    return { valid: false, error: `Value must be at most ${max}` };
  }

  return { valid: true, value: parsed };
}

/**
 * Validate that min element size is less than max element size
 *
 * @example
 * validateSizeRange(2, 10)  // {valid: true}
 * validateSizeRange(10, 2)  // {valid: false, error: 'Min size must be less than max size'}
 */
export function validateSizeRange(
  minSize: number,
  maxSize: number,
): ValidationResult {
  if (minSize >= maxSize) {
    return {
      valid: false,
      error: 'Min size must be less than max size',
    };
  }

  return { valid: true };
}

/**
 * Validate number input (general purpose)
 *
 * @example
 * validateNumber('10.5', { min: 0, max: 100 })
 */
export function validateNumber(
  value: string | number | null | undefined,
  options: ValidateNumberOptions = {},
): ValidationResult {
  const {
    min = -Infinity,
    max = Infinity,
    required = false,
    allowNegative = true,
  } = options;

  // Handle empty values
  if (value === null || value === undefined || value === '') {
    if (required) {
      return { valid: false, error: 'Value is required' };
    }
    return { valid: true, value: null };
  }

  const parsed = parseNumberExpression(value);

  if (isNaN(parsed) || !isFinite(parsed)) {
    return { valid: false, error: 'Invalid number' };
  }

  if (!allowNegative && parsed < 0) {
    return { valid: false, error: 'Value must be non-negative' };
  }

  if (parsed < min) {
    return { valid: false, error: `Value must be at least ${min}` };
  }

  if (parsed > max) {
    return { valid: false, error: `Value must be at most ${max}` };
  }

  return { valid: true, value: parsed };
}

/**
 * Validate file extension
 *
 * @example
 * validateFileExtension('model.step', ['.step', '.stp'])  // true
 * validateFileExtension('model.txt', ['.step', '.stp'])   // false
 */
export function validateFileExtension(
  filename: string,
  allowedExtensions: string[],
): boolean {
  if (!filename || typeof filename !== 'string') return false;

  const extension = filename.toLowerCase().substring(filename.lastIndexOf('.'));
  const normalizedExtensions = allowedExtensions.map((ext) =>
    ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  return normalizedExtensions.includes(extension);
}

/**
 * Sanitize string input (remove dangerous characters)
 *
 * @param input - Input to sanitize
 * @param options - Sanitization options
 * @returns Sanitized string
 */
export function sanitizeString(
  input: string,
  options: SanitizeStringOptions = {},
): string {
  const { maxLength = 1000 } = options;

  if (typeof input !== 'string') return '';

  // Trim whitespace
  let sanitized = input.trim();

  // Limit length
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }

  return sanitized;
}
